import FuturesBacktestCreation from '@/features/strategyBacktests/FuturesBacktestCreation';
import { useNavigate } from 'react-router-dom';
import FormSwitch from '@/components/UI/FormSwitch';
import { Button, Card, OptionBuyingStrategySelect } from '@/components/UI';
import StrategyBacktestOperations from '@/features/strategyBacktests/StrategyBacktestOperations';
import type { TradingStrategyFormData } from '../tradingStrategyEditorSubmitPayloads';

interface StrategyBacktestsPaneProps {
    formData: TradingStrategyFormData;
    isOwnedStrategy: boolean;
    onInputChange: <Field extends keyof TradingStrategyFormData>(field: Field, value: TradingStrategyFormData[Field]) => void;
    strategyHasOptionLeg: boolean;
    strategyId: number | null;
    validationErrors: Record<string, string>;
}

/**
 * Everything backtest-related for this strategy: the settings that decide how it is backtested, and
 * the coverage, queue and population operations that follow from them.
 *
 * Settings are staged and commit through the editor's save bar. Operations below run immediately —
 * queueing or deleting jobs is not editing configuration, so it does not belong to the save model.
 */
const StrategyBacktestsPane = ({
    formData,
    isOwnedStrategy,
    onInputChange,
    strategyHasOptionLeg,
    strategyId,
    validationErrors
}: StrategyBacktestsPaneProps) => {
    const navigate = useNavigate();
    const isFutures =
        formData.allowedUnderlyingEntityTypes.includes('FuturesProduct') ||
        formData.legs.some((leg) => !leg.isDeleted && leg.derivativeType === 'Future');

    return (
        <div className="flex flex-col gap-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h4 className="text-base font-semibold text-white">Backtests</h4>
                    <p className="mt-1 max-w-[58ch] text-xs text-zinc-500">
                        How this strategy is backtested, how much of its universe is covered, and what is queued right now.
                    </p>
                </div>
                {strategyId !== null && (
                    <div className="flex flex-wrap gap-2">
                        <Button
                            size="sm"
                            variant="outline-dark"
                            onClick={() => navigate('/tools/backtestParameterAnalysis', { state: { strategyId } })}
                        >
                            Parameter combinations
                        </Button>
                        <Button
                            size="sm"
                            variant="outline-dark"
                            onClick={() => navigate('/tools/filterBacktests', { state: { strategyId, autoSearch: false } })}
                        >
                            Filter backtests
                        </Button>
                    </div>
                )}
            </div>

            <Card subtitle="Applied by manual populate runs and by scheduled maintenance." title="Backtest settings">
                <div className="flex flex-col gap-4">
                    <FormSwitch
                        checked={formData.autoBacktest}
                        disabled={isFutures}
                        helpText={isFutures ? 'Derivative research uses manually launched, coverage-checked backtests.' : undefined}
                        label="Include in scheduled backtest maintenance"
                        onChange={(e) => onInputChange('autoBacktest', e.target.checked)}
                    />

                    {strategyHasOptionLeg && (
                        <OptionBuyingStrategySelect
                            error={validationErrors.autoBacktestOptionBuyingStrategiesOverride}
                            helpText="Leave empty only if every option leg sets its own override."
                            label="Option buying strategies (strategy default)"
                            mode="multi"
                            placeholder="Select option buying strategies..."
                            value={formData.autoBacktestOptionBuyingStrategiesOverride ?? []}
                            onChange={(selectedIds) => {
                                onInputChange(
                                    'autoBacktestOptionBuyingStrategiesOverride',
                                    selectedIds.length > 0 ? selectedIds : undefined
                                );
                            }}
                        />
                    )}
                </div>
            </Card>

            {strategyId === null ? (
                <Card title="Coverage and queue">
                    <div className="text-sm text-zinc-400">Create the strategy to start tracking backtest coverage.</div>
                </Card>
            ) : isFutures ? (
                <FuturesBacktestCreation
                    canManage={isOwnedStrategy}
                    strategyId={strategyId}
                    usesEtfSignals={formData.allowedUnderlyingEntityTypes.includes('Asset')}
                />
            ) : (
                <StrategyBacktestOperations canManage={isOwnedStrategy} strategyId={strategyId} />
            )}
        </div>
    );
};

export default StrategyBacktestsPane;
